import { KnowledgeAskCitation } from '@digital-twins/shared-types'
import {
  citationSupportsClaim,
  extractPlacementTokens,
} from './citationSupportsClaim'
import { isWebAssistantCitation } from './partitionAssistantCitations'

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * One citation reduced to a single insertion anchor.
 *
 * `providerRange` is kept only so the junk-offset check can still run on the
 * span the provider actually sent. Snapping collapses the span to a point, so
 * the check has to happen before it.
 */
interface CiteAnchor {
  index: number
  anchor: number
  providerRange: { start: number; end: number } | null
}

const CITE_LINK_RE = /\[([^\]]*)\]\(#cite-(\d+)\)/g

/** Answer text plus the partition of citations behind it. */
export interface CitationPlacement {
  /** Answer text with inline `[n](#cite-n)` markers for ANCHORED citations only. */
  text: string
  /** 1-based citation indexes rendered inline, ascending. */
  placed: Array<number>
  /** 1-based citation indexes with no anchor. Rendered in the rail, NEVER in prose. Ascending. */
  unplaced: Array<number>
}

/**
 * Inject bare `[n](#cite-n)` chips next to the grounded claim.
 *
 * The thread body stays synthesized insight text. Source excerpts belong in
 * the references column — never as highlighted answer spans.
 *
 * Placement order:
 * 1. Gemini `charStart`/`charEnd` answer spans, only when the snippet
 *    supports that sentence (`citationSupportsClaim`) → anchor at span end
 * 2. Else heuristic token match → anchor at the matched token in the brief
 * 3. Anything that still has no anchor is reported in `unplaced` and is never
 *    appended to the text. Unanchored chips are the references rail's job.
 *
 * Every anchor then goes through `snapChipToBoundary`, so a drifted offset can
 * never land mid-word or inside a markdown emphasis run. Citations that snap to
 * the same boundary render side by side rather than the later one being railed.
 *
 * `placed` and `unplaced` together cover every citation index exactly once.
 */
export function injectCitationMarkers(
  text: string,
  citations: Array<KnowledgeAskCitation>,
): CitationPlacement {
  // Defensive only: a `kind: 'web'` citation carries no charStart/charEnd/
  // snippet to anchor against and must never reach this function once a
  // caller runs `partitionAssistantCitations` first (the single shared seam
  // for both the live stream path and `messageToTurn` on reload — see
  // partitionAssistantCitations.ts). This is a backstop for a caller that
  // casts the raw mixed array straight into `Array<KnowledgeAskCitation>`
  // instead of partitioning: without it a web citation would silently
  // occupy an index meant for a document citation, and always fall through
  // to `unplaced`, throwing off numbering for every document citation after it.
  const safeCitations = citations.filter(
    (citation) => !isWebAssistantCitation(citation),
  )
  const total = safeCitations.length

  if (!text) {
    return partitionCitations(text || '', new Set<number>(), total)
  }

  // Persisted / previously wrapped `[sentence](#cite-n)` → sentence + chip.
  const unwrapped = unwrapCitationSpans(text)
  if (total === 0) {
    return partitionCitations(unwrapped, new Set<number>(), 0)
  }

  // Avoid double-injecting if markers already exist.
  if (/\]\(#cite-\d+\)/.test(unwrapped)) {
    return partitionCitations(
      unwrapped,
      citationIndexesPresent(unwrapped, total),
      total,
    )
  }

  const anchors: Array<CiteAnchor> = []
  const withoutOffsets: Array<{ index: number; citation: KnowledgeAskCitation }> =
    []

  safeCitations.forEach((citation, i) => {
    const index = i + 1
    const start =
      typeof citation.charStart === 'number' &&
      Number.isFinite(citation.charStart)
        ? citation.charStart
        : null
    const end =
      typeof citation.charEnd === 'number' && Number.isFinite(citation.charEnd)
        ? citation.charEnd
        : null

    if (end != null && end >= 0) {
      const clampedEnd = Math.min(end, text.length)
      const claimAt =
        start != null && start >= 0 && start < clampedEnd ? start : clampedEnd
      const claimRange = expandToSentence(text, claimAt)
      const claimText = text.slice(claimRange.start, claimRange.end)
      if (citationSupportsClaim(claimText, citation.snippet || '')) {
        const providerRange =
          start != null && start >= 0 && start < end
            ? { start: Math.min(start, text.length), end: clampedEnd }
            : null
        anchors.push({ index, anchor: clampedEnd, providerRange })
      } else {
        withoutOffsets.push({ index, citation })
      }
    } else {
      withoutOffsets.push({ index, citation })
    }
  })

  const occupiedEnds = new Set(anchors.map((a) => a.anchor))

  for (const { index, citation } of withoutOffsets) {
    const inferred = inferPlacementFromSnippet(
      text,
      citation.snippet || '',
      occupiedEnds,
    )
    if (inferred != null) {
      anchors.push({ index, anchor: inferred, providerRange: null })
      occupiedEnds.add(inferred)
    }
    // No inferred anchor: the index falls through to `unplaced`.
  }

  // A provider span that crosses a markdown block is document coordinates, not
  // answer coordinates. Drop it to the rail rather than trusting its end. This
  // runs before snapping, which would collapse the span and hide the signal.
  const usable = anchors.filter(
    (a) =>
      a.providerRange == null ||
      !isCrossBlockRange(text, a.providerRange.start, a.providerRange.end),
  )

  // Group by insertion point so co-located citations render side by side.
  const byPoint = new Map<number, Array<number>>()
  for (const { index, anchor } of usable) {
    const point = snapChipToBoundary(text, anchor)
    if (!isChipRenderableAt(text, point)) {
      // Falls through to `unplaced`: the rail beats a chip nobody can see.
      continue
    }
    const at = byPoint.get(point)
    if (at) {
      at.push(index)
    } else {
      byPoint.set(point, [index])
    }
  }

  // Apply from the end so earlier offsets stay valid.
  const points = [...byPoint.keys()].sort((a, b) => b - a)

  const placedIndexes = new Set<number>()
  let result = unwrapped
  for (const point of points) {
    const indexes = (byPoint.get(point) || []).sort((a, b) => a - b)
    if (indexes.length === 0) {
      continue
    }
    result = insertBareMarkers(result, point, indexes)
    indexes.forEach((index) => placedIndexes.add(index))
  }

  return partitionCitations(result, placedIndexes, total)
}

/**
 * Split `1..total` into the indexes that reached the text and the ones that did
 * not. Every index lands in exactly one list, both ascending.
 */
function partitionCitations(
  text: string,
  placedIndexes: Set<number>,
  total: number,
): CitationPlacement {
  const placed: Array<number> = []
  const unplaced: Array<number> = []
  for (let index = 1; index <= total; index += 1) {
    if (placedIndexes.has(index)) {
      placed.push(index)
    } else {
      unplaced.push(index)
    }
  }
  return { text, placed, unplaced }
}

/** 1-based citation indexes already rendered as `[n](#cite-n)` in the text. */
function citationIndexesPresent(text: string, total: number): Set<number> {
  const present = new Set<number>()
  CITE_LINK_RE.lastIndex = 0
  let match = CITE_LINK_RE.exec(text)
  while (match != null) {
    const index = Number(match[2])
    if (Number.isInteger(index) && index >= 1 && index <= total) {
      present.add(index)
    }
    match = CITE_LINK_RE.exec(text)
  }
  return present
}

/**
 * Convert `[cited sentence](#cite-n)` wraps into plain sentence + `[n](#cite-n)`.
 * Leaves already-bare chips unchanged.
 */
export function unwrapCitationSpans(text: string): string {
  if (!text || !text.includes('#cite-')) {
    return text
  }
  CITE_LINK_RE.lastIndex = 0
  return text.replace(CITE_LINK_RE, (full, label: string, n: string) => {
    if (label.trim() === n) {
      return full
    }
    const restored = unescapeMdLinkText(label)
    const needsSpace = restored.length > 0 && !/\s$/.test(restored)
    return `${restored}${needsSpace ? ' ' : ''}[${n}](#cite-${n})`
  })
}

/**
 * Insert one or more chips at a single boundary. Citations that snapped to the
 * same point render side by side, ascending, instead of the later ones being
 * demoted to the references rail.
 */
function insertBareMarkers(
  text: string,
  at: number,
  indexes: Array<number>,
): string {
  const clamped = Math.max(0, Math.min(at, text.length))
  const markers = indexes.map((index) => `[${index}](#cite-${index})`).join(' ')
  const needsSpace = clamped > 0 && !/\s/.test(text[clamped - 1] || '')
  const insert = `${needsSpace ? ' ' : ''}${markers}`
  return `${text.slice(0, clamped)}${insert}${text.slice(clamped)}`
}

/** Double quotes only. Apostrophes are far too common inside ordinary prose. */
const OPEN_QUOTES = '"\u201c'
const CLOSE_QUOTES = '"\u201d'

/**
 * Move a raw anchor to the boundary a reader expects a chip at.
 *
 * The chip belongs at the end of the claim, so the default is the end of the
 * containing sentence, or the end of the line when the line carries no terminal
 * punctuation (a bullet fragment). That is structurally never mid-word and
 * never inside a markdown emphasis run, which is what makes a drifted provider
 * offset harmless.
 *
 * The one exception is a direct quotation: an anchor inside `"..."` gets its
 * chip right after the closing quote, next to the words it verifies.
 */
export function snapChipToBoundary(text: string, anchor: number): number {
  const clamped = Math.max(0, Math.min(anchor, text.length))

  const quoted = quoteBoundary(text, clamped)
  if (
    quoted != null &&
    isCleanBoundary(text, quoted) &&
    !isInsideMarkdownLink(text, quoted)
  ) {
    return quoted
  }

  // An anchor sitting ahead of its own line's first word cannot refer to that
  // line: nothing has been claimed yet. It belongs to the line above. This is
  // the drifted-offset case that used to render the chip in between the '**'
  // and a bold bullet label.
  const backward = previousLineEnd(text, clamped)
  if (backward != null) {
    return backward
  }

  let end = expandToSentence(text, clamped).end
  while (end > 0 && /[\r\t ]/.test(text[end - 1] || '')) {
    end -= 1
  }
  return end
}

/** True when `at` falls inside a `[label](target)` construct on its own line. */
function isInsideMarkdownLink(text: string, at: number): boolean {
  const lineStart = text.lastIndexOf('\n', Math.max(0, at - 1)) + 1
  const lineEndRaw = text.indexOf('\n', at)
  const lineEnd = lineEndRaw < 0 ? text.length : lineEndRaw
  const line = text.slice(lineStart, lineEnd)
  const linkRe = /\[[^\]]*\]\([^)]*\)/g
  let match = linkRe.exec(line)
  while (match != null) {
    const start = lineStart + match.index
    if (at > start && at < start + match[0].length) {
      return true
    }
    match = linkRe.exec(line)
  }
  return false
}

/**
 * False for boundaries the renderer would swallow or mangle.
 *
 * A chip appended to a GFM table row lands past the last column and is dropped
 * outright: worse than a wrong position, because the reader loses the citation
 * entirely. Inside a fenced block it renders as literal markdown, and a heading
 * labels a section rather than making a claim. All three go to the rail, where
 * an unanchored citation is still reachable.
 */
function isChipRenderableAt(text: string, at: number): boolean {
  const lineStart = text.lastIndexOf('\n', Math.max(0, at - 1)) + 1
  const lineEndRaw = text.indexOf('\n', at)
  const lineEnd = lineEndRaw < 0 ? text.length : lineEndRaw
  const line = text.slice(lineStart, lineEnd)

  if (/^\s*\|.*\|\s*$/.test(line)) {
    return false
  }
  if (/^\s{0,3}#{1,6}\s/.test(line)) {
    return false
  }
  const fencesBefore = (text.slice(0, lineStart).match(/^\s*```/gm) || []).length
  if (fencesBefore % 2 === 1) {
    return false
  }
  return true
}

/**
 * End of the closest preceding line that has content, but only when `anchor`
 * still sits ahead of its own line's first content character. Null otherwise.
 */
function previousLineEnd(text: string, anchor: number): number | null {
  const lineStart = text.lastIndexOf('\n', Math.max(0, anchor - 1)) + 1
  if (lineStart === 0) {
    return null
  }

  // Past the list marker and any emphasis openers, to the first real character.
  // Ordered markers count: `1.` bullets are as common as `-` in model answers,
  // and `expandToSentence` would otherwise read the marker's dot as a sentence
  // end and park the chip in front of the label.
  let contentStart = lineStart
  const prefix = text
    .slice(lineStart)
    .match(/^\s*(?:(?:[-*\u2022]|\d{1,4}[.)])\s+)?[*_]*/)
  if (prefix) {
    contentStart += prefix[0].length
  }
  if (anchor > contentStart) {
    return null
  }

  let end = lineStart - 1
  while (end > 0 && /\s/.test(text[end - 1] || '')) {
    end -= 1
  }
  if (end <= 0) {
    return null
  }
  // A heading is a label for what follows, not a claim. Chips do not go in it.
  const prevLineStart = text.lastIndexOf('\n', end - 1) + 1
  if (/^\s{0,3}#{1,6}\s/.test(text.slice(prevLineStart, end))) {
    return null
  }
  return end
}

/**
 * End of the quoted span containing `anchor`, past any punctuation and emphasis
 * markers that close with it. Null when the anchor is not inside a quotation.
 */
function quoteBoundary(text: string, anchor: number): number | null {
  const lineStart = text.lastIndexOf('\n', Math.max(0, anchor - 1)) + 1
  const lineEndRaw = text.indexOf('\n', anchor)
  const lineEnd = lineEndRaw < 0 ? text.length : lineEndRaw

  let open: number | null = null
  for (let i = lineStart; i < lineEnd; i += 1) {
    const ch = text[i] || ''
    if (open == null) {
      if (OPEN_QUOTES.includes(ch)) {
        open = i
      }
      continue
    }
    if (!CLOSE_QUOTES.includes(ch)) {
      continue
    }
    if (open < anchor && anchor <= i) {
      let end = i + 1
      while (end < lineEnd && /[,.;:!?]/.test(text[end] || '')) {
        end += 1
      }
      while (end < lineEnd && /[*_]/.test(text[end] || '')) {
        end += 1
      }
      return end
    }
    open = null
  }
  return null
}

/** True when a boundary splits neither a word nor a markdown emphasis run. */
function isCleanBoundary(text: string, at: number): boolean {
  if (at <= 0 || at >= text.length) {
    return true
  }
  const before = text[at - 1] || ''
  const after = text[at] || ''
  if (/\w/.test(before) && /\w/.test(after)) {
    return false
  }
  if (/[*_]/.test(before) && /[*_]/.test(after)) {
    return false
  }
  return true
}

/** True when a wrap would cross a blank line or list boundary (breaks CommonMark links). */
export function isCrossBlockRange(
  text: string,
  start: number,
  end: number,
): boolean {
  if (start < 0 || end <= start || end > text.length) {
    return true
  }
  const slice = text.slice(start, end)
  if (/\n\s*\n/.test(slice)) {
    return true
  }
  // List item start after a newline inside the span.
  if (/\n\s*[-*•]\s+/.test(slice)) {
    return true
  }
  return false
}

function unescapeMdLinkText(value: string): string {
  return value.replace(/\\\[/g, '[').replace(/\\\]/g, ']').replace(/\\\\/g, '\\')
}

/**
 * Expand an anchor offset to the containing sentence / bullet line.
 */
export function expandToSentence(
  text: string,
  anchor: number,
): { start: number; end: number } {
  const clamped = Math.max(0, Math.min(anchor, text.length))

  let start = 0
  for (let i = clamped - 1; i >= 0; i -= 1) {
    const ch = text[i]
    if (ch === '\n') {
      start = i + 1
      break
    }
    if (
      (ch === '.' || ch === '!' || ch === '?') &&
      (i + 1 >= text.length || /\s/.test(text[i + 1] || ''))
    ) {
      start = i + 1
      break
    }
  }
  while (start < clamped && /\s/.test(text[start] || '')) {
    start += 1
  }
  // Skip list markers so highlight starts on the claim text.
  const listPrefix = text.slice(start).match(/^[-*•]\s+/)
  if (listPrefix) {
    start += listPrefix[0].length
  }

  let end = clamped
  for (let i = clamped; i < text.length; i += 1) {
    const ch = text[i]
    if (ch === '\n') {
      end = i
      break
    }
    if (
      (ch === '.' || ch === '!' || ch === '?') &&
      (i + 1 >= text.length || /\s/.test(text[i + 1] || ''))
    ) {
      end = i + 1
      break
    }
    end = i + 1
  }

  if (end <= start) {
    return { start: clamped, end: clamped }
  }
  return { start, end }
}

/**
 * Find a char index in `answer` after a distinctive snippet token that also
 * appears in the answer. Prefer percentages / multi-digit numbers, then
 * longer words.
 */
export function inferPlacementFromSnippet(
  answer: string,
  snippet: string,
  occupied: Set<number> = new Set(),
): number | null {
  if (!answer || !snippet) return null

  const candidates = extractPlacementTokens(snippet)
  const haystack = answer.toLowerCase()
  for (const token of candidates) {
    const needle = token.toLowerCase()
    let from = 0
    while (from < answer.length) {
      const idx = haystack.indexOf(needle, from)
      if (idx < 0) break

      const before = idx > 0 ? answer[idx - 1] : ''
      const after = answer[idx + token.length] || ''
      // Avoid splitting ranges like "18-29" on a bare "18".
      const insideNumberRange =
        /\d/.test(before) ||
        after === '-' ||
        (before === '-' && /\d/.test(after))
      if (insideNumberRange && !token.includes('%')) {
        from = idx + token.length
        continue
      }

      let end = idx + token.length
      // Keep the anchor inside the claim sentence. expandToSentence will
      // include trailing punctuation; swallowing ". " here jumps to the next sentence.
      if (!occupied.has(end)) {
        return end
      }
      from = idx + token.length
    }
  }
  return null
}

export function citationHrefIndex(href: string | undefined): number | null {
  if (!href) return null
  const match = /^#cite-(\d+)$/.exec(href)
  if (!match) return null
  const n = Number(match[1])
  return Number.isFinite(n) && n > 0 ? n : null
}

export function isCitationUuid(value: string | null | undefined): boolean {
  if (!value) return false
  return UUID_RE.test(value.trim())
}

/** True when the markdown link label is only the citation number (no span wrap). */
export function isBareCitationLabel(childrenText: string, index: number): boolean {
  return childrenText.trim() === String(index)
}
